import { useState } from 'react'
import { BusinessRow } from './BusinessRow'
import { Pagination } from '@/components/common'
import { useBusinesses } from '@/hooks/useBusiness'

interface BusinessListProps {
  onBusinessSelect: (id: string) => void
  onCreateClick: () => void
}

const PAGE_SIZE = 50

export function BusinessList({ onBusinessSelect, onCreateClick }: BusinessListProps) {
  const [page, setPage] = useState(1)

  const { data, isLoading, error, refetch } = useBusinesses({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  })

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="error-state">
        <p className="text-red-800">Failed to load businesses</p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="business-list">
      {/* Header with Create button */}
      <div className="flex justify-end">
        <button
          onClick={onCreateClick}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          data-testid="create-business-btn"
        >
          Create Business
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8" data-testid="loading-state">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : !data?.businesses?.length ? (
        <div className="text-center py-8 text-gray-500" data-testid="empty-state">
          No businesses found
        </div>
      ) : (
        <>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Accounting ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alternative Names
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credit Cards
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    {/* Action column */}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.businesses.map((business) => (
                  <BusinessRow
                    key={business.id}
                    business={business}
                    onClick={() => onBusinessSelect(business.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              className="mt-4"
            />
          )}

          <div className="text-sm text-gray-500 text-center">
            Showing {data.businesses.length} of {data.total} businesses
          </div>
        </>
      )}
    </div>
  )
}
